// Canonical adjuster button for mobile redesign. Wraps the existing button
// styling but adds:
//
// - 44×44 minimum hit area on mobile (Apple HIG / WCAG 2.5.5) by default;
//   combat-critical callers (HP, Mana, end turn, damage commit) pass
//   `mobileSize={48}` to clear the 9.2 mm empirical one-handed thumb
//   threshold (Parhi/Karlson/Bederson, MobileHCI 2006). MOBILE-REDESIGN.md
//   §3 Rule 3 codifies the routine-vs-combat-critical split.
// - Press state (scale + brightness) on touchstart, not touchend, so the
//   user feels the tap registered before the action commits — closes the
//   <100ms HIG feedback budget.
// - Long-press ramp-up. Holding the button fires modifyHp at increasing
//   magnitudes: 1, 1, 1, 5, 5, 5, 10, 10, … so the player can "tune" HP
//   without typing a number for big swings.
// - Haptic + audio feedback on Android via the feedback() helper. iOS
//   Safari can't vibrate (Web Vibration API is no-op); the visual press
//   state stays the primary signal there.
// - prefers-reduced-motion: scale + transition disabled when the user
//   has it set; everything else (long-press, feedback) still works.
//
// Used by ResourceBar's ±HP / ±Mana buttons in Phase A.0.4. The same
// component will plug into Mana, Food count, item Qty, ability uses, and
// any other adjuster across the player + DM views.

import React, { useEffect, useRef, useState } from 'react';
import { feedback } from './feedback';
import useReducedMotion from './useReducedMotion';

// Tick magnitude schedule for held buttons. Index = tick number; value =
// absolute amount (sign applied separately). Slow start (1, 1, 1) keeps
// fine adjustments precise; mid-press (5, 5) covers typical combat damage
// numbers; sustained press (10) covers party-wide "they all took 30" type
// adjustments without the player typing.
const RAMP_SCHEDULE = [1, 1, 1, 5, 5, 5, 10];

// First-tick delay (after the immediate tap) before ramp-up begins. Short
// enough to feel responsive on a held press, long enough to not double-
// fire on a quick stab.
const HOLD_DELAY_MS = 350;

// Inter-tick interval during a sustained hold. ~8 ticks/sec.
const TICK_INTERVAL_MS = 120;

export default function AdjustButton({
  onAdjust,
  sign = 1,
  baseAmount = null,
  feedbackType = 'tap',
  isMobile = false,
  mobileSize = 44,
  ariaLabel,
  style,
  children,
  ...rest
}) {
  const reduceMotion = useReducedMotion();
  const [pressed, setPressed] = useState(false);

  const tickTimerRef = useRef(null);
  const tickCountRef = useRef(0);
  const heldRef = useRef(false);

  const fireTick = () => {
    const idx = Math.min(tickCountRef.current, RAMP_SCHEDULE.length - 1);
    const amount = baseAmount != null && baseAmount > 0 ? baseAmount : RAMP_SCHEDULE[idx];
    onAdjust(amount * sign);
    feedback(tickCountRef.current === 0 ? feedbackType : 'longpress-tick');
    tickCountRef.current += 1;
  };

  const startHold = () => {
    if (heldRef.current) return;
    heldRef.current = true;
    setPressed(true);
    tickCountRef.current = 0;
    fireTick();
    tickTimerRef.current = setTimeout(function loop() {
      fireTick();
      tickTimerRef.current = setTimeout(loop, TICK_INTERVAL_MS);
    }, HOLD_DELAY_MS);
  };

  const endHold = () => {
    heldRef.current = false;
    setPressed(false);
    if (tickTimerRef.current) {
      clearTimeout(tickTimerRef.current);
      tickTimerRef.current = null;
    }
    tickCountRef.current = 0;
  };

  useEffect(() => () => {
    if (tickTimerRef.current) clearTimeout(tickTimerRef.current);
  }, []);

  const minSize = isMobile ? mobileSize : undefined;
  const pressed01 = pressed && !reduceMotion;

  return (
    <button
      {...rest}
      aria-label={ariaLabel}
      style={{
        ...style,
        minWidth: minSize != null ? `${minSize}px` : style?.minWidth,
        minHeight: minSize != null ? `${minSize}px` : style?.minHeight,
        transform: pressed01 ? 'scale(0.95)' : 'scale(1)',
        filter: pressed01 ? 'brightness(1.15)' : 'brightness(1)',
        transition: reduceMotion ? 'none' : 'transform 80ms ease-out, filter 80ms ease-out',
        touchAction: 'manipulation',
        userSelect: 'none',
        WebkitTapHighlightColor: 'transparent',
      }}
      onTouchStart={(e) => { e.preventDefault(); startHold(); }}
      onTouchEnd={(e) => { e.preventDefault(); endHold(); }}
      onTouchCancel={endHold}
      onMouseDown={startHold}
      onMouseUp={endHold}
      onMouseLeave={() => { if (heldRef.current) endHold(); }}
    >
      {children}
    </button>
  );
}
